import { useRef, useState, useCallback } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { getVideoEmbedInfo } from '@/lib/videoEmbed';

interface VideoBackgroundProps {
  url: string;
  /** Loop the video (default: true). */
  loop?: boolean;
  /** CSS object-fit for the video layer (default: 'cover'). */
  fit?: 'cover' | 'contain';
  /**
   * Renders a floating mute/unmute button for visitors.
   * The video ALWAYS starts muted (browser autoplay policy).
   * When true, a pill button lets users opt-in to sound via postMessage.
   * Default: false — correct for editor/preview contexts.
   */
  showSoundToggle?: boolean;
  /** Icon/button tint color — usually settings.text_color. */
  accentColor?: string;
}

/**
 * Video background that fills its positioned parent.
 *
 * Audio strategy:
 * - Embed URL always carries mute=1 so autoplay works in all browsers.
 * - If showSoundToggle=true (public bio only), a floating pill button lets
 *   visitors unmute without a page reload:
 *     YouTube → postMessage command API (requires enablejsapi=1 in URL)
 *     Vimeo   → postMessage Player API
 *     File    → direct videoRef.muted mutation
 */
export default function VideoBackground({
  url,
  loop = true,
  fit = 'cover',
  showSoundToggle = false,
  accentColor,
}: VideoBackgroundProps) {
  const info = getVideoEmbedInfo(url, { loop });
  const [isMuted, setIsMuted] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const videoRef  = useRef<HTMLVideoElement>(null);

  const handleToggleMute = useCallback(() => {
    const next = !isMuted;
    setIsMuted(next);

    if (info?.type === 'file') {
      if (videoRef.current) videoRef.current.muted = next;
      return;
    }

    const win = iframeRef.current?.contentWindow;
    if (!win) return;

    if (info?.type === 'youtube') {
      win.postMessage(
        JSON.stringify({ event: 'command', func: next ? 'mute' : 'unMute', args: [''] }),
        'https://www.youtube.com',
      );
    } else if (info?.type === 'vimeo') {
      win.postMessage(
        JSON.stringify({ method: 'setMuted', value: next }),
        'https://player.vimeo.com',
      );
    }
  }, [isMuted, info?.type]);

  if (!info) return null;

  // ── Floating sound toggle pill ──────────────────────────────────────────
  const soundButton = showSoundToggle ? (
    <button
      type="button"
      onClick={handleToggleMute}
      title={isMuted ? 'Ativar som' : 'Silenciar'}
      className="absolute bottom-4 right-4 z-20 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium shadow-lg backdrop-blur-sm transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
      style={{
        background: 'rgba(0,0,0,0.45)',
        color: accentColor || '#fff',
        pointerEvents: 'all',
      }}
    >
      {isMuted
        ? <VolumeX className="h-3.5 w-3.5 shrink-0" />
        : <Volume2 className="h-3.5 w-3.5 shrink-0" />
      }
      {isMuted ? 'Som desativado' : 'Som ativado'}
    </button>
  ) : null;

  // ── File video ───────────────────────────────────────────────────────────
  if (info.type === 'file') {
    return (
      <>
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full"
          style={{ zIndex: 0, pointerEvents: 'none', objectFit: fit }}
          autoPlay
          muted   // always starts muted; handleToggleMute flips the ref
          loop={loop}
          playsInline
          src={url}
        />
        {soundButton}
      </>
    );
  }

  // ── YouTube / Vimeo iframe ───────────────────────────────────────────────
  const iframeStyle = fit === 'contain'
    ? { width: '100%', height: '100%' }
    : { width: '300%', height: '300%', transform: 'translate(-50%, -50%)' };

  return (
    <>
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ zIndex: 0, pointerEvents: 'none' }}
      >
        <iframe
          ref={iframeRef}
          src={info.embedUrl}
          title="Video Background"
          className={fit === 'contain' ? 'w-full h-full' : 'absolute top-1/2 left-1/2'}
          style={{ ...iframeStyle, border: 'none', pointerEvents: 'none' }}
          allow="autoplay; encrypted-media; picture-in-picture"
        />
      </div>
      {soundButton}
    </>
  );
}
